"use client"

import Image from "next/image"
import { useFrontendPreferences } from "@lib/frontend-preferences"
import type { StorefrontSettings } from "@lib/data/storefront-settings"
import { getLocalizedTaxonomyTitle } from "@lib/product-content-localization"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type LinkItem = { id: string; handle: string; title: string }

export default function ClientFooter({
  categories,
  collections,
  storefrontSettings,
}: {
  categories: LinkItem[]
  collections: LinkItem[]
  storefrontSettings?: StorefrontSettings | null
}) {
  const { locale, t } = useFrontendPreferences()
  const supportHeading = locale === "fa" ? storefrontSettings?.support_heading_fa : storefrontSettings?.support_heading_en
  const supportBody = locale === "fa" ? storefrontSettings?.support_body_fa : storefrontSettings?.support_body_en
  const supportHours = locale === "fa" ? storefrontSettings?.support_hours_fa : storefrontSettings?.support_hours_en
  const address = locale === "fa" ? storefrontSettings?.address_fa : storefrontSettings?.address_en
  const socialLinks = [
    { key: "instagram", url: storefrontSettings?.instagram_url, label: storefrontSettings?.instagram_label || "Instagram" },
    { key: "bale", url: storefrontSettings?.bale_url, label: storefrontSettings?.bale_label || "Bale" },
    { key: "telegram", url: storefrontSettings?.telegram_url, label: storefrontSettings?.telegram_label || "Telegram" },
  ].filter((item): item is { key: string; url: string; label: string } => Boolean(item.url))

  return (
    <footer className="sahil-source-footer">
      <div className="content-container sahil-source-footer__inner">
        <div className="sahil-source-footer__intro">
          <LocalizedClientLink
            href="/"
            className="sahil-source-footer__brand"
            dir="ltr"
          >
            <Image
              src="/assets/sahil/sahil_shell_icon_transparent.png"
              alt=""
              width={43}
              height={43}
              className="sahil-source-footer__icon"
            />
            <Image
              src="/assets/sahil/sahil_wordmark_transparent.png"
              alt="Sahil"
              width={89}
              height={43}
              className="sahil-source-footer__wordmark"
            />
          </LocalizedClientLink>
          <p>{t.footer.intro}</p>
        </div>

        <div className="sahil-source-footer__links">
          {categories.length > 0 && (
            <div>
              <span>{t.footer.categoryHeading}</span>
              <ul data-testid="footer-categories">
                {categories.map((category) => (
                  <li key={category.id}>
                    <LocalizedClientLink
                      href={`/categories/${category.handle}`}
                    >
                      {getLocalizedTaxonomyTitle(category.title, locale)}
                    </LocalizedClientLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {collections.length > 0 && (
            <div>
              <span>{t.footer.collectionsHeading}</span>
              <ul>
                {collections.map((collection) => (
                  <li key={collection.id}>
                    <LocalizedClientLink
                      href={`/collections/${collection.handle}`}
                    >
                      {getLocalizedTaxonomyTitle(collection.title, locale)}
                    </LocalizedClientLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <span>{t.footer.shoreHeading}</span>
            <ul>
              <li>
                <LocalizedClientLink href="/store">
                  {t.footer.allScarves}
                </LocalizedClientLink>
              </li>
              <li>
                <LocalizedClientLink href="/account">
                  {t.footer.account}
                </LocalizedClientLink>
              </li>
              <li>
                <LocalizedClientLink href="/cart">
                  {t.footer.shoppingBag}
                </LocalizedClientLink>
              </li>
            </ul>
          </div>
        </div>
        {(supportHeading || supportBody || supportHours || storefrontSettings?.phone || storefrontSettings?.email || address || socialLinks.length > 0) && (
          <div className="sahil-source-footer__links mt-8" dir={locale === "fa" ? "rtl" : "ltr"}>
            {(supportHeading || supportBody || supportHours) && <div><span>{supportHeading || (locale === "fa" ? "پشتیبانی" : "Support")}</span><ul>{supportBody && <li>{supportBody}</li>}{supportHours && <li>{supportHours}</li>}</ul></div>}
            {(storefrontSettings?.phone || storefrontSettings?.email || address) && <div><span>{locale === "fa" ? "تماس و نشانی" : "Contact"}</span><ul>{storefrontSettings?.phone && <li><a href={`tel:${storefrontSettings.phone}`}>{storefrontSettings.phone}</a></li>}{storefrontSettings?.email && <li><a href={`mailto:${storefrontSettings.email}`}>{storefrontSettings.email}</a></li>}{address && <li>{address}</li>}</ul></div>}
            {socialLinks.length > 0 && <div><span>{locale === "fa" ? "ما را دنبال کنید" : "Follow us"}</span><ul>{socialLinks.map((item) => <li key={item.key}><a href={item.url} target="_blank" rel="noreferrer">{item.label}</a></li>)}</ul></div>}
          </div>
        )}
      </div>
      <div className="content-container sahil-source-footer__bottom">
        <span>© {new Date().getFullYear()} Sahil</span>
        <span>{t.footer.madeFor}</span>
        <span>EUR</span>
      </div>
    </footer>
  )
}
